import { tw } from "@careinfo/tailwind";
import React from "react";
import { cn } from "../../../lib/utils";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "ghost";
export type ButtonSize = "medium" | "small" | "icon" | "iconSmall";

export type ButtonProps = {
  variant?: ButtonVariant;
  active?: boolean;
  activeClassname?: string;
  buttonSize?: ButtonSize;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      active,
      buttonSize = "medium",
      children,
      disabled,
      variant = "primary",
      className,
      activeClassname,
      type = "button",
      ...rest
    },
    ref
  ) => {
    const buttonClassName = cn(
      "flex group items-center justify-center border border-transparent gap-2 text-sm font-semibold rounded-md disabled:opacity-50 whitespace-nowrap",

      variant === "primary" &&
        cn(
          "text-white !bg-primary border-black",
          !disabled &&
            !active &&
            "hover:bg-primary-light active:bg-primary-light",
          active && cn("bg-primary-light", activeClassname)
        ),

      variant === "secondary" &&
        cn(
          "text-primary",
          !disabled &&
            !active &&
            "hover:bg-primary-light active:bg-primary-light",
          active && "bg-primary-light"
        ),

      variant === "tertiary" &&
        cn(
          "bg-primary-light text-primary",
          !disabled &&
            !active &&
            "hover:bg-primary-light active:bg-primary-light",
          active && cn("bg-primary-light", activeClassname)
        ),

      variant === "ghost" &&
        cn(
          "bg-transparent border-transparent text-primary",
          !disabled &&
            !active &&
            "hover:bg-primary-light hover:text-primary active:bg-primary-light active:text-primary",
          active && cn("bg-black/10 text-primary ", activeClassname)
        ),

      buttonSize === "medium" && "py-2 px-3",
      buttonSize === "small" && "py-1 px-2",
      buttonSize === "icon" && "w-8 h-8",
      buttonSize === "iconSmall" && "w-6 h-6",

      className
    );

    return (
      <button
        ref={ref}
        disabled={disabled}
        type={type}
        className={tw`${buttonClassName}`}
        {...rest}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";
